import React, { useRef, useState } from "react";
import { GRID, RIGHT_PADDING_PERCENT } from "../model/constants";
import type { ZoomRange, CrosshairPosition } from "../model/types";
import type { ServerBasePrice } from "services/types";
import type { CandleWithFlag } from "../model/types";

interface UseChartInteractionParams {
  canvasRef: React.RefObject<HTMLCanvasElement>;
  containerRef: React.RefObject<HTMLDivElement>;
  zoomRangeRef: React.MutableRefObject<ZoomRange>;
  chartLayoutRef: React.MutableRefObject<{
    width: number;
    height: number;
    left: number;
    right: number;
    top: number;
    bottom: number;
  }>;
  dataRef: React.MutableRefObject<(ServerBasePrice | CandleWithFlag)[]>;
  crosshairPosRef: React.MutableRefObject<CrosshairPosition | null>;
  hoverCandleIndexRef: React.MutableRefObject<number | null>;
  autoScrollEnabledRef: React.MutableRefObject<boolean>;
  setZoomRange: React.Dispatch<React.SetStateAction<ZoomRange>>;
  setUserZoomed: React.Dispatch<React.SetStateAction<boolean>>;
  onNeedsRedraw: () => void;
}

export function useChartInteraction({
  canvasRef,
  containerRef,
  zoomRangeRef,
  chartLayoutRef,
  dataRef,
  crosshairPosRef,
  hoverCandleIndexRef,
  autoScrollEnabledRef,
  setZoomRange,
  setUserZoomed,
  onNeedsRedraw,
}: UseChartInteractionParams) {
  const [isDragging, setIsDragging] = useState(false);
  const isDraggingRef = useRef(false);
  const dragStartXRef = useRef<number | null>(null);
  const dragStartZoomRef = useRef<ZoomRange | null>(null);

  const handleMouseMove = (e: React.MouseEvent<HTMLCanvasElement>) => {
    const canvas = canvasRef.current;
    const rect = canvas?.getBoundingClientRect();
    if (!rect) return;

    const x = e.clientX - rect.left;
    const y = e.clientY - rect.top;
    crosshairPosRef.current = { x, y };

    const { start, end } = zoomRangeRef.current;
    const layout = chartLayoutRef.current;
    const chartWidth = layout.width - layout.left - layout.right;
    const visibleCount = Math.floor(end) - Math.floor(start) + 1;
    const candleWidth = chartWidth / visibleCount;
    const visibleIndex = Math.floor((x - layout.left) / candleWidth);
    const dataIndex = Math.floor(start) + visibleIndex;

    if (
      visibleIndex >= 0 &&
      visibleIndex < visibleCount &&
      dataIndex < dataRef.current.length
    ) {
      hoverCandleIndexRef.current = dataIndex;
    } else {
      hoverCandleIndexRef.current = null;
    }

    if (isDraggingRef.current && dragStartXRef.current !== null && dragStartZoomRef.current) {
      setUserZoomed(true);
      autoScrollEnabledRef.current = false;

      const dx = x - dragStartXRef.current;
      const { start: zStart, end: zEnd } = dragStartZoomRef.current;
      const visibleCount = zEnd - zStart + 1;
      const candleWidth = chartWidth / visibleCount;
      const shift = dx / candleWidth;

      let newStart = zStart - shift;
      let newEnd = zEnd - shift;

      const lastCandleIndex = dataRef.current.length - 1;
      const rightPadding = Math.ceil(
        (chartWidth * RIGHT_PADDING_PERCENT) / candleWidth,
      );
      const maxEnd = lastCandleIndex + rightPadding;

      if (newEnd > maxEnd) {
        newEnd = maxEnd;
        newStart = newEnd - visibleCount + 1;
      }
      if (newStart < 0) {
        newStart = 0;
        newEnd = visibleCount - 1;
      }

      setZoomRange({ start: newStart, end: newEnd });
    }

    onNeedsRedraw();
  };

  const handleMouseLeave = () => {
    crosshairPosRef.current = null;
    hoverCandleIndexRef.current = null;
    onNeedsRedraw();
  };

  const handleMouseDown = (e: React.MouseEvent<HTMLCanvasElement>) => {
    const rect = canvasRef.current?.getBoundingClientRect();
    if (!rect) return;

    const x = e.clientX - rect.left;
    isDraggingRef.current = true;
    setIsDragging(true);
    dragStartXRef.current = x;
    dragStartZoomRef.current = zoomRangeRef.current;
  };

  const handleMouseUp = () => {
    isDraggingRef.current = false;
    setIsDragging(false);
    dragStartXRef.current = null;

    // Re-enable auto-scroll if the last candle is still within the visible range.
    // If the user dragged past the last candle (into old history), keep it disabled.
    if (dataRef.current.length > 0) {
      const lastCandleIndex = dataRef.current.length - 1;
      if (lastCandleIndex <= Math.ceil(zoomRangeRef.current.end)) {
        autoScrollEnabledRef.current = true;
      }
    }
  };

  const handleWheel = (e: React.WheelEvent<HTMLCanvasElement>) => {
    setUserZoomed(true);
    autoScrollEnabledRef.current = false;

    const delta = e.deltaY > 0 ? 1 : -1;

    setZoomRange((prev) => {
      const currentRange = prev.end - prev.start;
      let newRange = currentRange + delta * 10;

      const minRange = 24;
      const maxRange = Math.min(dataRef.current.length + 50, 300);
      newRange = Math.max(minRange, Math.min(maxRange, newRange));

      // Zoom around the current view's center so the visible area doesn't jump.
      const center = (prev.start + prev.end) / 2;
      let start = center - newRange / 2;
      let end = center + newRange / 2;

      const containerWidth = containerRef.current?.clientWidth || 800;
      const chartWidth = containerWidth - GRID.left - GRID.right;
      const candleWidth = chartWidth / newRange;
      const rightPadding = Math.ceil((chartWidth * RIGHT_PADDING_PERCENT) / candleWidth);
      const maxEnd = dataRef.current.length - 1 + rightPadding;

      if (end > maxEnd) {
        end = maxEnd;
        start = end - newRange + 1;
      }
      if (start < 0) {
        start = 0;
        end = start + newRange - 1;
      }

      return { start, end };
    });
  };

  return {
    isDragging,
    handleMouseMove,
    handleMouseLeave,
    handleMouseDown,
    handleMouseUp,
    handleWheel,
  };
}
